/**
 * Default-database resolution for the add-tile dialog's database picker.
 *
 * The form owns the tile's `database` (addTileFormModel.ts); this module
 * answers "what should it START as" when a NEW tile is created or the dialog
 * connection changes. Resolution order:
 *   1. the connection's CONFIGURED default database (saved on the connection)
 *   2. sqlite → the primary database is always literally 'main' (same
 *      convention as the sqlite language service's SQLITE_DEFAULT_DATABASE)
 *   3. the first database of the connection's known database list
 *   4. undefined — the picker renders the amber "No database" state and the
 *      user picks one (the user-reported gap: a pgsql/mssql connection saved
 *      without a default database used to dead-end the Visual tab).
 */
import { readDatabaseListingThroughCache } from '../../../../database-objects/hooks/useDatabaseQueries';

export function resolveDefaultTileDatabase(args: {
  engine: string | undefined;
  configuredDatabase: string | undefined;
  knownDatabases: readonly string[];
}): string | undefined {
  const configured = args.configuredDatabase?.trim();
  if (configured) return configured;
  if (args.engine === 'sqlite') return 'main';
  return args.knownDatabases[0];
}

/**
 * The connection's known database list, for step 3 above. Only an `'ok'`
 * listing is trusted — on `'error'` the service's array is the engine
 * fallback (configured DB + hardcoded system list, Slice 5b) and defaulting
 * to e.g. `master` from it would silently point the tile at the wrong
 * database. Mirrors `resolveDatabase.ts`'s trust rule.
 */
export async function listKnownDatabases(
  connectionId: string,
  engine: string | undefined,
): Promise<string[]> {
  try {
    // Shared listing slot (see `readDatabaseListingThroughCache`): the add-tile
    // dialog opening is a user action, and its answer warms the very cache the
    // dialog's own database picker reads.
    const result = await readDatabaseListingThroughCache(connectionId, engine ?? 'mssql');
    return result.state === 'ok' ? result.databases : [];
  } catch {
    return [];
  }
}
